import React, { useCallback, useMemo, useState } from "react"
import { LoaderCircleIcon, SaveIcon, Eye, EyeOff, Minimize2, XCircleIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"

export type PanelType =
  | "filters"
  | "recordSummary"
  | "evidences"
  | "recordDetails"
  | "filesList"
  | "agreementPeriods"
  | "voiceForm"
  | "voicePlayer"
  | "summaryNotes"
  | "find"
  | "details"
  | "recordView"
  | "checks"

export type PanelConfigState = "hidden" | "closed" | "conditional"

export interface PanelsConfig {
  [key: string]: {
    state: PanelConfigState
    order?: number
  }
}

interface PanelInfo {
  type: PanelType
  label: string
}

export const panelDefinitions: PanelInfo[] = [
  { type: "filters", label: "Filters" },
  { type: "recordSummary", label: "Record Summary" },
  { type: "evidences", label: "Evidences" },
  { type: "recordDetails", label: "Record Details" },
  { type: "filesList", label: "Files List" },
  { type: "agreementPeriods", label: "Agreement Periods" },
  { type: "voiceForm", label: "Voice Form" },
  { type: "voicePlayer", label: "Voice Player" },
  { type: "summaryNotes", label: "Summary Notes" },
  { type: "find", label: "Find" },
  { type: "details", label: "Details" },
  { type: "recordView", label: "Record View" },
  { type: "checks", label: "Checks" },
]

const stateOptions: Array<{ value: PanelConfigState; label: string }> = [
  { value: "hidden", label: "Hidden" },
  { value: "closed", label: "Closed" },
  { value: "conditional", label: "Conditional" },
]

function buildInitialPanelsConfig(config: PanelsConfig | undefined): PanelsConfig {
  const initial: PanelsConfig = {}
  panelDefinitions.forEach((panel, i) => {
    initial[panel.type] = config?.[panel.type] ?? { state: "closed", order: i }
  })
  return initial
}

export interface SharedPanelsTabProps {
  config: PanelsConfig | undefined
  onSave: (panelsConfig: PanelsConfig) => Promise<void> | void
  onRemove: () => Promise<void> | void
  isFetching: boolean
  currentAction?: "SAVE_SECTION" | "REMOVE_SECTION" | null
}

export function SharedPanelsTab({ config, onSave, onRemove, isFetching, currentAction }: SharedPanelsTabProps) {
  const [panelsConfig, setPanelsConfig] = useState<PanelsConfig>(() => buildInitialPanelsConfig(config))

  const handleStateChange = useCallback((type: PanelType, state: PanelConfigState) => {
    setPanelsConfig((prev) => ({
      ...prev,
      [type]: { ...prev[type], state },
    }))
  }, [])

  const handleOrderChange = useCallback((type: PanelType, order: number) => {
    setPanelsConfig((prev) => ({
      ...prev,
      [type]: { ...prev[type], order },
    }))
  }, [])

  const handleSave = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      onSave(panelsConfig)
    },
    [onSave, panelsConfig],
  )

  const handleRemoveClick = useCallback(() => {
    onRemove()
  }, [onRemove])

  const sortedPanels = useMemo(() => {
    return panelDefinitions
      .map((p) => ({ ...p, config: panelsConfig[p.type] }))
      .sort((a, b) => (a.config?.order ?? 999) - (b.config?.order ?? 999))
  }, [panelsConfig])

  const showSaveLoading = isFetching && currentAction === "SAVE_SECTION"
  const showRemoveLoading = isFetching && currentAction === "REMOVE_SECTION"

  const getStateIcon = (state: PanelConfigState) => {
    switch (state) {
      case "hidden":
        return <EyeOff className="h-4 w-4" />
      case "closed":
        return <Minimize2 className="h-4 w-4" />
      case "conditional":
        return <Eye className="h-4 w-4" />
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mx-auto w-full space-y-6">
            <div className="space-y-3">
              {sortedPanels.map((panel) => (
                <div
                  key={panel.type}
                  className="flex items-center justify-between gap-4 rounded-lg border bg-primary-foreground p-4"
                >
                  <div className="flex items-center gap-3">
                    <h4 className="font-medium text-primary">{panel.label}</h4>
                  </div>

                  <div className="flex items-center gap-3">
                    <Select
                      value={panel.config?.state ?? "closed"}
                      onValueChange={(value) => handleStateChange(panel.type, value as PanelConfigState)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {stateOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <span className="flex items-center gap-2">
                              {getStateIcon(option.value)}
                              {option.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="flex items-center gap-2">
                      <label className="text-sm text-muted-foreground">Order:</label>
                      <input
                        type="number"
                        min="0"
                        value={panel.config?.order ?? ""}
                        onChange={(e) => handleOrderChange(panel.type, Number.parseInt(e.target.value, 10) || 0)}
                        className="w-16 rounded border px-2 py-1 text-center text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="flex-shrink-0 border-t p-4">
          <div className="mx-auto flex w-full items-center gap-3">
            <Button type="submit" disabled={isFetching} className="flex-1 bg-info hover:bg-info/90">
              {showSaveLoading && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
              <SaveIcon className="h-4 w-4" />
              Save Panel Configuration
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  disabled={isFetching}
                >
                  {showRemoveLoading && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                  <XCircleIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Remove Section</span>
                  <span className="sm:hidden">Remove</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Remove Panels Configuration?</p>
                  <p className="text-sm text-muted-foreground">
                    This will remove all panel configurations and reset all panels to default state (closed).
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <PopoverClose asChild>
                      <Button type="button" variant="ghost" size="sm">
                        Cancel
                      </Button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleRemoveClick}
                        disabled={isFetching}
                      >
                        Remove
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </form>
      </div>
    </div>
  )
}
